/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DashboardPropertiesV2API
 * @hideconstructor
 * @classdesc Internal API class that is used to manage the Dashboard Properties
 */
define([], () => {
	class DashboardPropertiesV2API {

		/**
		 * @function DashboardPropertiesV2API#activate
		 * @description activate the feature; When the feature is activated it will start listening on canvas selection changes and adjust the inner context accordingly
		 */
		activate() {}

		/**
		 * @function DashboardPropertiesV2API#deactivate
		 * @description deactivate the feature; When the feature is deactivated it stops listening on selections and stays dormant until activated again
		 */
		deactivate() {}

		/**
		 * @function DashboardPropertiesV2API#getContext
		 * @description get the observable context
		 * @returns {Object} observable object with keys: definition and ancestors
		 * @example {
		 * 	"definition": {
		 * 		"id": "definition id 1",
		 * 		"label": "Definition #1",
		 * 		"properties": [], // property list
		 * 		"layout": [] // property layout list
		 * 	},
		 * 	"ancestors": [
		 * 		{
		 * 			"id": "parent definition id",
		 * 			"label": "Parent definition"
		 * 		}
		 * 	]
		 * }
		 */
		getContext() {}

		/**
		 * @function DashboardPropertiesV2API#addContext
		 * @description Add a new UI child context and navigate to it. If the child context has the same id as the last added context, it will be replaced instead of being added at the bottom of the list
		 * The contexts are stored in a queue (LIFO) data structure. Last item in the list is the current context displayed in the list.
		 * @param {Object} context - new child context to be added
		 * @param {string} context.id - context id
		 * @param {array} [context.contents] - array of contents to be taken into account when setPropertyValue is called; if no contents are being passed with a new context the selected contents will get the setPropertyValue call
		 */
		addContext() {}

		/**
		 * @function DashboardPropertiesV2API#navigateBack
		 * @description Navigating one context up in the queue. The context that was previously current will be removed.
		 */
		navigateBack() {}

		/**
		 * @function DashboardPropertiesV2API#refresh
		 * @description Refresh the observable context; should be used as a last resort when the property list and layout list needs to be refreshed
		 * One use case is when we have a property that doesn't go through the content setPropertyValue method, which needs a re-poll of the state.
		 */
		refresh() {}

		/**
		 * @function DashboardPropertiesV2API#reset
		 * @description Reset current context with a specific context; if none specified, default to the parent panel context based on selections
		 * @param {Object} [context={ id: "parentPanel" }] context to reset to
		 */
		reset() {}

		/**
		 * @function DashboardPropertiesV2API#setPropertyValue
		 * @description Sets the value of a property on the selection content
		 * @param {String} name - Name of the property to set
		 * @param {Object} value - Value to set
		 * @param {TransactionToken} [transactionToken] - transaction token to use
		 */
		setPropertyValue() {}

		/**
		 * @function DashboardPropertiesV2API#updatePropertySpec
		 * @description Update the property spec. The method updates the property metadata located in editor.uiControl[key]
		 * @param {String} id - Property id to update
		 * @param {String} key - Property key to update
		 * @param {*} value - Property value to update to
		 * @example propertiesFeature.updatePropertySpec('fitPage', 'readOnly', true); // marks the "fitPage" property disabled
		 */
		updatePropertySpec() {}
	}

	return DashboardPropertiesV2API;
});
